import React, { useRef, useState } from "react";
import { Formik } from "formik";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import {
  Row,
  Col,
  Input,
  Checkbox,
  Select,
  DatePicker,
  Button as AntdButton,
  Spin,
  notification,
} from "antd";
import NumberFormat from "react-number-format";
import moment from "moment";
import { Container, Title } from "./styles";
import { exportExcel } from "../../../services/excelExportService";
import { logEvent, logEventError } from "../../../utils/logger";
import ButtonStandard from "../button";

const { Option } = Select;

const OPERATORS = [
  { value: "EQ", label: "Igual" },
  { value: "NEQ", label: "Diferente" },
  { value: "GT", label: "Maior que" },
  { value: "LT", label: "Menor que" },
  { value: "GTE", label: "Maior ou igual" },
  { value: "LTE", label: "Menor ou igual" },
  { value: "BETWEEN", label: "Entre" },
];

const ExcelExportModal = ({
  visible,
  onClose,
  columns = null,
  filters = null,
  reportName,
  fileName,
  defaultFilters = null,
}) => {
  const formikRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const initialValues = {
    exportName: "",
    selectedColumns: columns
      ? Object.keys(columns).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      : {},
    filters: filters
      ? filters.map((filter) => ({
          field: filter.field,
          label: filter.label,
          type: filter.type,
          operator: filter.operator || "EQ",
          value:
            filter.type === "Options" || filter.type === "options"
              ? filter.value || []
              : filter.value || null,
          value2: null, // Para BETWEEN
        }))
      : [],
  };

  const validationSchema = Yup.object().shape({
    exportName: Yup.string().required(
      translation.excelExport?.validation?.exportNameRequired ||
        "Nome do relatório é obrigatório"
    ),
  });

  const handleSubmit = async (values) => {
    logEvent("ExcelExportModal submit started", { reportName }, "info");
    setIsLoading(true);

    try {
      // Preparar colunas selecionadas no formato Map<String, String>
      // Se não houver colunas selecionadas, enviar todas
      const selectedColumnsMap = {};
      if (columns && values.selectedColumns) {
        const hasSelected = Object.values(values.selectedColumns).some(
          (v) => v === true
        );
        if (hasSelected) {
          Object.keys(values.selectedColumns).forEach((key) => {
            if (values.selectedColumns[key]) {
              selectedColumnsMap[key] = columns[key];
            }
          });
        } else {
          // Se nenhuma foi selecionada, enviar todas
          Object.keys(columns).forEach((key) => {
            selectedColumnsMap[key] = columns[key];
          });
        }
      }

      // Preparar filtros
      const processedFilters = values.filters
        .filter((filter) => {
          // Filtrar apenas filtros com valor preenchido
          if (filter.type === "Options" || filter.type === "options") {
            return Array.isArray(filter.value) && filter.value.length > 0;
          }
          if (
            filter.type === "date" ||
            filter.type === "Date" ||
            filter.type === "number" ||
            filter.type === "Number"
          ) {
            if (filter.operator === "BETWEEN") {
              return filter.value != null && filter.value2 != null;
            }
            return filter.value != null;
          }
          return filter.value != null && filter.value !== "";
        })
        .map((filter) => {
          const filterObj = {
            field: filter.field,
            label: filter.label,
            type: filter.type,
            operator: filter.operator,
          };

          if (filter.type === "date" || filter.type === "Date") {
            if (filter.operator === "BETWEEN") {
              filterObj.value = [
                moment(filter.value).format("YYYY-MM-DD"),
                moment(filter.value2).format("YYYY-MM-DD"),
              ];
            } else {
              filterObj.value = moment(filter.value).format("YYYY-MM-DD");
            }
          } else if (filter.type === "number" || filter.type === "Number") {
            if (filter.operator === "BETWEEN") {
              filterObj.value = [filter.value, filter.value2];
            } else if (filter.operator === "IN") {
              filterObj.value = Array.isArray(filter.value)
                ? filter.value
                : [filter.value];
            } else {
              filterObj.value = filter.value;
            }
          } else if (filter.type === "Options" || filter.type === "options") {
            filterObj.value = Array.isArray(filter.value) ? filter.value : [];
          } else {
            filterObj.value = filter.value;
          }

          return filterObj;
        });

      if (defaultFilters) {
        processedFilters.push(...defaultFilters);
      }
      const body = {
        exportName: values.exportName,
        columns:
          Object.keys(selectedColumnsMap).length > 0
            ? selectedColumnsMap
            : undefined,
        filters: processedFilters.length > 0 ? processedFilters : undefined,
      };

      logEvent("ExcelExportModal sending request", { body }, "info");

      const { data } = await exportExcel({
        groupId,
        farmId,
        reportName,
        fileName,
        body,
      });

      // Fazer download do arquivo
      const downloadUrl = window.URL.createObjectURL(
        new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      );
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `${fileName || "export"}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      logEvent("ExcelExportModal download success", { fileName }, "info");

      notification.success({
        message:
          translation.excelExport?.successMessage ||
          "Arquivo exportado com sucesso!",
      });
      formikRef.current.resetForm(initialValues);
      onClose();
    } catch (error) {
      logEventError("ExcelExportModal export error", error);
      notification.error({
        message:
          translation.excelExport?.errorMessage ||
          "Erro ao exportar arquivo. Tente novamente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (formikRef.current) {
      logEvent("ExcelExportModal reset form", { initialValues }, "info");
      formikRef.current.resetForm(initialValues);
    }
    onClose();
  };

  const renderFilterField = (
    filter,
    index,
    values,
    setFieldValue,
    errors,
    submitCount
  ) => {
    const filterValue = values.filters[index];
    const hasOperator =
      filter.type === "number" ||
      filter.type === "Number" ||
      filter.type === "date" ||
      filter.type === "Date";
    const isBetween = filterValue.operator === "BETWEEN";

    return (
      <Col key={index} span={isBetween ? 12 : 12} style={{ marginBottom: 16 }}>
        <Row type="flex" gutter={8}>
          <Col span={24} style={{ marginBottom: 8 }}>
            <label className="filterLabel">{filter.label}</label>
          </Col>
          {hasOperator && (
            <Col span={8} style={{ marginBottom: 8 }}>
              <Select
                value={filterValue.operator}
                onChange={(operator) => {
                  setFieldValue(`filters[${index}].operator`, operator);
                  if (operator !== "BETWEEN" && operator !== "IN") {
                    setFieldValue(`filters[${index}].value2`, null);
                  }
                  if (operator === "BETWEEN") {
                    setFieldValue(`filters[${index}].value`, null);
                    setFieldValue(`filters[${index}].value2`, null);
                  }
                }}
              >
                {OPERATORS.map((op) => (
                  <Option key={op.value} value={op.value}>
                    {translation.excelExport?.operators?.[op.value] || op.label}
                  </Option>
                ))}
              </Select>
            </Col>
          )}
          {(filter.type === "date" || filter.type === "Date") &&
          filterValue.operator === "BETWEEN" ? (
            <Col span={16}>
              <Row type="flex" gutter={8}>
                <Col span={24}>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={filterValue.value ? moment(filterValue.value) : null}
                    placeholder={translation.defaultDatePickerPlaceholder}
                    format="DD/MM/YYYY"
                    onChange={(date) => {
                      setFieldValue(`filters[${index}].value`, date);
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <label className="filterLabel">até</label>
              </Row>
              <Row type="flex" gutter={8}>
                <Col span={24}>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={
                      filterValue.value2 ? moment(filterValue.value2) : null
                    }
                    placeholder={translation.defaultDatePickerPlaceholder}
                    format="DD/MM/YYYY"
                    onChange={(date) => {
                      setFieldValue(`filters[${index}].value2`, date);
                    }}
                  />
                </Col>
              </Row>
            </Col>
          ) : filter.type === "date" || filter.type === "Date" ? (
            <Col span={hasOperator ? 16 : 24}>
              <DatePicker
                style={{ width: "100%" }}
                value={filterValue.value ? moment(filterValue.value) : null}
                placeholder={translation.defaultDatePickerPlaceholder}
                format="DD/MM/YYYY"
                onChange={(date) => {
                  setFieldValue(`filters[${index}].value`, date);
                }}
              />
            </Col>
          ) : (filter.type === "number" || filter.type === "Number") &&
            filterValue.operator === "BETWEEN" ? (
            <Col span={16}>
              <Row type="flex" gutter={8}>
                <Col span={24}>
                  <NumberFormat
                    customInput={Input}
                    value={filterValue.value || ""}
                    placeholder={translation.defaultPlaceholder}
                    decimalScale={2}
                    decimalSeparator=","
                    thousandSeparator="."
                    onValueChange={({ floatValue }) => {
                      setFieldValue(`filters[${index}].value`, floatValue);
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <label className="filterLabel">até</label>
              </Row>
              <Row type="flex" gutter={8}>
                <Col span={24}>
                  <NumberFormat
                    customInput={Input}
                    value={filterValue.value2 || ""}
                    placeholder={translation.defaultPlaceholder}
                    decimalScale={2}
                    decimalSeparator=","
                    thousandSeparator="."
                    onValueChange={({ floatValue }) => {
                      setFieldValue(`filters[${index}].value2`, floatValue);
                    }}
                  />
                </Col>
              </Row>
            </Col>
          ) : (filter.type === "number" || filter.type === "Number") &&
            filterValue.operator === "IN" ? (
            <Col span={hasOperator ? 16 : 24}>
              <Input
                placeholder={
                  translation.excelExport?.placeholders?.inOperator ||
                  "Separado por vírgulas"
                }
                value={
                  Array.isArray(filterValue.value)
                    ? filterValue.value.join(",")
                    : filterValue.value || ""
                }
                onChange={(e) => {
                  const values = e.target.value
                    .split(",")
                    .map((v) => parseFloat(v.trim()))
                    .filter((v) => !isNaN(v));
                  setFieldValue(`filters[${index}].value`, values);
                }}
              />
            </Col>
          ) : filter.type === "number" || filter.type === "Number" ? (
            <Col span={hasOperator ? 16 : 24}>
              <NumberFormat
                customInput={Input}
                value={filterValue.value || ""}
                placeholder={translation.defaultPlaceholder}
                decimalScale={2}
                decimalSeparator=","
                thousandSeparator="."
                onValueChange={({ floatValue }) => {
                  setFieldValue(`filters[${index}].value`, floatValue);
                }}
              />
            </Col>
          ) : filter.type === "String" || filter.type === "string" ? (
            <Col span={24}>
              <Input
                value={filterValue.value || ""}
                placeholder={translation.defaultPlaceholder}
                onChange={(e) => {
                  setFieldValue(`filters[${index}].value`, e.target.value);
                }}
              />
            </Col>
          ) : filter.type === "Options" || filter.type === "options" ? (
            <Col span={24}>
              <Select
                mode="multiple"
                value={Array.isArray(filterValue.value) ? filterValue.value : []}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.props.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                placeholder={translation.defaultPlaceholder}
                onChange={(selectedValues) => {
                  setFieldValue(`filters[${index}].value`, selectedValues);
                }}
              >
                {(filter.options || []).map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.value}
                  </Option>
                ))}
              </Select>
            </Col>
          ) : null}
        </Row>
      </Col>
    );
  };

  return (
    <Container
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={770}
      centered
      maskClosable={false}
    >
      <Spin spinning={isLoading}>
        <Formik
          ref={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, setFieldValue, handleSubmit, submitCount }) => (
            <form onSubmit={handleSubmit}>
              <Row type="flex" gutter={16}>
                <Col span={24}>
                  <Title>
                    {translation.excelExport?.modalTitle ||
                      "Exportar para Excel"}
                  </Title>
                </Col>
              </Row>

              {/* Campo exportName */}
              <Row type="flex" gutter={8} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Row>
                    <label
                      className={
                        errors.exportName && submitCount > 0
                          ? "error"
                          : "filterLabel"
                      }
                    >
                      {translation.excelExport?.fields?.exportName ||
                        "Nome do Relatório"}
                      *
                    </label>
                  </Row>
                  <Row>
                    <Input
                      value={values.exportName}
                      placeholder={
                        translation.excelExport?.placeholders?.exportName ||
                        "Digite o nome do relatório"
                      }
                      onChange={(e) =>
                        setFieldValue("exportName", e.target.value)
                      }
                    />
                  </Row>
                </Col>
              </Row>

              <Row
                type="flex"
                justify="end"
                gutter={8}
                style={{ marginTop: 8 }}
              >
                <Col>
                  <ButtonStandard
                    type="button"
                    buttonType="primary"
                    style={{ marginRight: 8 }}
                    onClick={() => {
                      const allSelected = {};
                      if (columns && Object.keys(columns).length > 0) {
                        Object.keys(columns).forEach((key) => {
                          allSelected[key] = true;
                        });
                        setFieldValue("selectedColumns", allSelected);
                      }
                    }}
                  >
                    {translation.excelExport?.fields?.selectAllColumns ||
                      "Marcar todas"}
                  </ButtonStandard>
                </Col>
                <Col>
                  <ButtonStandard
                    type="button"
                    buttonType="secondary"
                    onClick={() => {
                      if (columns && Object.keys(columns).length > 0) {
                        setFieldValue("selectedColumns", {});
                      }
                    }}
                  >
                    {translation.excelExport?.fields?.deselectAllColumns ||
                      "Desmarcar todas"}
                  </ButtonStandard>
                </Col>
              </Row>
              {/* Checkboxes de colunas */}
              {columns && Object.keys(columns).length > 0 && (
                <Row type="flex" gutter={8} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <Row>
                      <label className="filterLabel">
                        {translation.excelExport?.fields?.columns || "Colunas"}
                      </label>
                    </Row>
                    <Row type="flex" gutter={16}>
                      {Object.entries(columns).map(([key, alias]) => (
                        <Col key={key} span={8}>
                          <Checkbox
                            checked={values.selectedColumns[key] || false}
                            onChange={(e) => {
                              setFieldValue(
                                `selectedColumns[${key}]`,
                                e.target.checked
                              );
                            }}
                          >
                            {alias}
                          </Checkbox>
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              )}

              {/* Filtros */}
              {filters && filters.length > 0 && (
                <>
                  <Row type="flex" gutter={8} style={{ marginTop: 16 }}>
                    <Col span={24}>
                      <label className="filterLabel">
                        {translation.excelExport?.fields?.filters || "Filtros"}
                      </label>
                    </Col>
                  </Row>
                  <Row type="flex" gutter={8} style={{ marginTop: 8 }}>
                    {filters.map((filter, index) =>
                      renderFilterField(
                        filter,
                        index,
                        values,
                        setFieldValue,
                        errors,
                        submitCount
                      )
                    )}
                  </Row>
                </>
              )}

              {/* Botões */}
              <Row
                type="flex"
                justify="end"
                gutter={8}
                style={{ marginTop: 24 }}
              >
                <Col>
                  <AntdButton onClick={handleCancel}>
                    {translation.buttons?.cancel || "Cancelar"}
                  </AntdButton>
                </Col>
                <Col>
                  <AntdButton
                    type="primary"
                    htmlType="submit"
                    style={{
                      backgroundColor: "#199d58",
                      borderColor: "#199d58",
                    }}
                  >
                    {translation.excelExport?.buttons?.export || "Exportar"}
                  </AntdButton>
                </Col>
              </Row>
            </form>
          )}
        </Formik>
      </Spin>
    </Container>
  );
};

export default ExcelExportModal;
